import * as React from 'react';
import styled from 'styled-components';

import { formatPhoneNumber, SUPPORT_EMAIL } from 'utils';
import { Text, Link } from 'components';
import type { JsonObject } from '@/app-package';
import { Flex, Thumbnail } from '@bb/elements-web';
import { Org } from '@bb/automaton';
import { useBreakpoint } from 'hooks';

export type AnsweringOrgType = JsonObject<Org>;

const ANSWERING_SERVICE_PROVIDERS = [
  'APTO CONNECT',
  'EXTEND COMMUNICATIONS INC.',
  'CANCOM ANSWERING SOLUTIONS INC.',
  'ACCUTEL',
  'CALL CENTRE SYSTEMS INC.',
  'TOUCH COMMUNICATIONS INC.',
  'GLOBAL CONNECT CALL CENTRE',
  'CLIENTIME',
  'FUTURE TODAY',
];

type AnswerServiceProps = { answeringOrg: AnsweringOrgType };

/** The old, legacy Answering Service page from before we introduced Appointment Desk & Live Concierge.
 *  We only keep this page around for select brokerages under PropTX to use. */
const AnsweringServiceInfo: React.FunctionComponent<AnswerServiceProps> = ({
  answeringOrg,
}) => {
  const { md } = useBreakpoint();
  if (answeringOrg?.id) {
    return (
      <StyledFlex $md={md}>
        <Thumbnail
          src={`${answeringOrg.logoUrl}`}
          size="large"
          dataCy="ansOrg-logo"
          square
        />
        <div className="answering-contact">
          <h4>{answeringOrg.name}</h4>
          <label>Address:</label>
          <p>{answeringOrg.addresses[0][0].address}</p>
          <label>Phone:</label>
          <p>{formatPhoneNumber(answeringOrg.addresses[0][0].phone)}</p>
        </div>
      </StyledFlex>
    );
  }

  return (
    <StyledContainer>
      <StyledText $bold block>
        Your brokerage is NOT configured to use an answering service provider
        with Supra.
      </StyledText>
      <br />
      <StyledText block>
        To setup an answering service on Supra,{' '}
        <StyledSpan>please contact your Answering Service provider</StyledSpan>{' '}
        and have them reach out to us at{' '}
        <StyledLink href={`mailto:${SUPPORT_EMAIL}`}>
          {' '}
          {SUPPORT_EMAIL}
        </StyledLink>{' '}
        with the date that your brokerage will begin using their services with
        Supra.
      </StyledText>
      <br />
      <StyledText $bold block>
        Approved Answering Service Providers:
      </StyledText>
      <br />
      <ul>
        {ANSWERING_SERVICE_PROVIDERS.map(item => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </StyledContainer>
  );
};

export default AnsweringServiceInfo;
const StyledFlex = styled(Flex)<{ $md: boolean }>`
  max-width: ${({ $md }) => ($md ? '50%' : '100%')};
  padding: ${({ $md }) => ($md ? '20' : '10')}px;
  justify-content: center;
  & > div {
    margin-left: 20px;
  }
`;
const StyledContainer = styled.div`
  font-size: 14px;
  margin: 20px 30px 0;
  max-width: 800px;
`;

const StyledText = styled(Text)<{ $bold?: boolean }>`
  ${({ $bold }) => $bold && 'font-weight: 700;'}
  color: #6a6c6f;
`;

const StyledSpan = styled.span`
  text-decoration: underline;
`;

const StyledLink = styled(Link)`
  font-weight: 700;
  && {
    color: #6a6c6f;
  }
`;
